// Allowlist of element attributes that the "Update Collection" propagation
// replays across sibling designs, keyed by element type. Each attribute maps
// to the restriction key in elementsRestrictionsMap that, when present on a
// sibling element, blocks that attribute from being replayed on that sibling.
//
// Position (left/top/width/height) and font size are intentionally excluded
// from this allowlist and are NOT propagated by "Update Collection":
// replaying geometry across differently-sized siblings drifts their layouts.
// Do NOT add left/top/width/height here.
//
// The `shadow` attribute uses the legacy restriction key `outline` (the UI
// label predates the attribute rename; renaming the restriction key would
// thrash saved templates' restriction state).
//
// `imageInstructions` is deliberately excluded; it ships in a follow-up PR.
//
// `cssAnimation` is in the allowlist but does NOT ride the generic
// `elementAttributeChange` handler — it dispatches via `cssAnimationChange`
// because the sibling's page.animatedElements map must be rebuilt alongside
// the attribute write. `elementAttributeChangeAction` routes cssAnimation
// through `applyCssAnimation` as a defense-in-depth guard.

type AttributeRestrictionMap = Record<string, string>;

const BASE: AttributeRestrictionMap = {
  opacity: "opacity",
  blendMode: "blendMode",
  angle: "angle",
  isHidden: "visibility",
  shadow: "outline",
  cssAnimation: "animations"
};

// Only `color` and `fillColors` are stored as scalar element attributes —
// other color-family names (`fillColor` singular, `strokeColor`, `strokeWidth`)
// exist only as SVG render props on icon components and would never appear in
// an attribute payload. Add them back here alongside the handlers that write
// them to element state.
const COLOR_ATTRIBUTES: AttributeRestrictionMap = {
  color: "color",
  fillColors: "color"
};

export const PROPAGATABLE_ATTRIBUTES_BY_TYPE: Record<
  string,
  AttributeRestrictionMap
> = {
  // `textOutline` shares the legacy `outline` restriction key with `shadow`
  // — both are "outline" effects on text. Locking Outline on an element
  // blocks both from propagating, which matches the single UI tile.
  textbox: { ...BASE, ...COLOR_ATTRIBUTES, textOutline: "outline" },
  image: { ...BASE, borderRadius: "borderRadius" },
  // `isMuted` shares the `visibility` restriction key with `isHidden` — the
  // single Show/Hide tile on the sidebar governs both operations.
  video: { ...BASE, isMuted: "visibility" },
  vector: {
    ...BASE,
    ...COLOR_ATTRIBUTES,
    borderRadius: "borderRadius"
  },
  grid: { ...BASE, borderRadius: "borderRadius" },
  line: {
    ...BASE,
    color: "color",
    strokeDashArray: "lineStyle",
    strokeLinecap: "lineStyle"
  }
};
